import I18n from 'i18n-js';
import gql from 'graphql-tag';
import React from 'react';
import { ChildProps } from 'react-apollo';
import classnames from 'classnames';

import { formatAmount } from '@reverbdotcom/commons/src/money';
import { RCLoadingBars } from '@reverbdotcom/commons/src/rc/RCLoadingBars';
import { withUserContext, IUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import { convertMoney } from '@reverbdotcom/commons/src/convert_currency';
import PriceDisplay from '@reverbdotcom/commons/src/components/price_display';
import PriceDisplayRange from '@reverbdotcom/commons/src/components/price_display_range';
import { conditionUuidForSlug, NON_FUNCTIONING, BRAND_NEW } from '@reverbdotcom/commons/src/condition_helpers';
import { CoreRecommendedPriceRange } from '../../schemas/rql';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';

interface IProps extends IUserContext {
  conditionSlug?: string;
  canonicalProduct?: {
    title: string;
    id: string | number;
  };
  transactionsPanel?: React.ReactNode;
  roundedBorder?: boolean;
}

export function getPriceRange({ priceRecommendations, currency, exchangeRates }) {
  if (!priceRecommendations.length) { return {}; }

  const { priceLow, priceHigh } = priceRecommendations[0] || {};

  if (!priceLow || !priceHigh) { return {}; }

  let exchangedLow;
  let exchangedHigh;

  try {
    exchangedLow = convertMoney(priceLow, currency, exchangeRates);
    exchangedHigh = convertMoney(priceHigh, currency, exchangeRates);
  } catch { // an error is thrown if currency is not found in exchangeRates
    return {};
  }

  if (!exchangedLow || !exchangedHigh) { return {}; }

  const displayLow = formatAmount(exchangedLow, { precision: 0 });
  const displayHigh = formatAmount(exchangedHigh, { precision: 0 });

  const formattedPriceLow = {
    amountCents: exchangedLow.amountCents,
    currency: exchangedLow.currency,
    display: displayLow,
  };

  const formattedPriceHigh = {
    amountCents: exchangedHigh.amountCents,
    currency: exchangedHigh.currency,
    display: displayHigh,
  };

  return { formattedPriceLow, formattedPriceHigh };
}

function invalidCondition(conditionSlug) {
  return !conditionSlug || [BRAND_NEW, NON_FUNCTIONING].includes(conditionSlug);
}

export function RecommendedPriceRange({
  data,
  canonicalProduct,
  conditionSlug,
  transactionsPanel,
  user,
  roundedBorder,
}: ChildProps<IProps, CoreRecommendedPriceRange.Query>) {
  const emptyRender = !canonicalProduct?.id || invalidCondition(conditionSlug);

  if (emptyRender) { return null; }

  const { me, exchangeRates, priceRecommendations, loading } = data;

  if (loading) {
    return (
      <div className="d-flex fx-justify-center ptb-4">
        <RCLoadingBars />
      </div>
    );
  }

  const currency = me?.shop?.currency || user.currency;
  const { formattedPriceLow, formattedPriceHigh } = getPriceRange({
    priceRecommendations: priceRecommendations?.priceRecommendations || [],
    exchangeRates: exchangeRates?.rates || [],
    currency,
  });

  if (!formattedPriceLow?.display || !formattedPriceHigh?.display) { return null; }

  const classes = classnames(
    'recommended-price-range',
    { 'recommended-price-range--rounded-border': roundedBorder },
  );

  return (
    <div className={classes}>
      <h3 className="recommended-price-range__title">
        {I18n.t('discovery.recommendedPriceRange.title', { title: canonicalProduct.title, condition: I18n.t(`discovery.conditions.${conditionSlug}`) })}
      </h3>
      <ol className="recommended-price-range__list">
        <li className="recommended-price-range__range recommended-price-range__range--below">
          <div className="recommended-price-range__range_contents">
            <PriceDisplay
              amountCents={formattedPriceLow.amountCents}
              currency={formattedPriceLow.currency}
              display={I18n.t('discovery.recommendedPriceRange.belowPriceDisplay', { displayPrice: formattedPriceLow.display })}
            />
            <span className="recommended-price-range__label">
              {I18n.t('discovery.recommendedPriceRange.belowRange')}
            </span>
          </div>
        </li>
        <li className="recommended-price-range__range recommended-price-range__range--fair">
          <div className="recommended-price-range__range_contents">
            <PriceDisplayRange
              moneyLow={{
                amountCents: formattedPriceLow.amountCents,
                currency: formattedPriceLow.currency,
              }}
              moneyHigh={{
                amountCents: formattedPriceHigh.amountCents,
                currency: formattedPriceHigh.currency,
              }}
              displayConfig={{ precision: 0 }}
            />
            <span className="recommended-price-range__label">
              {I18n.t('discovery.recommendedPriceRange.fairRange')}
            </span>
          </div>
        </li>
        <li className="recommended-price-range__range recommended-price-range__range--above">
          <div className="recommended-price-range__range_contents">
            <PriceDisplay
              amountCents={formattedPriceHigh.amountCents}
              currency={formattedPriceHigh.currency}
              display={I18n.t('discovery.recommendedPriceRange.abovePriceDisplay', { displayPrice: formattedPriceHigh.display })}
            />
            <span className="recommended-price-range__label">
              {I18n.t('discovery.recommendedPriceRange.aboveRange')}
            </span>
          </div>
        </li>
      </ol>
      {transactionsPanel}
    </div>
  );
}

const withQuery = withGraphql<IProps, CoreRecommendedPriceRange.Query>(
  gql`
    query Core_RecommendedPriceRange(
      $priceRecommendationQueries: [Input_reverb_pricing_PriceRecommendationQuery]
      $loggedOut: Boolean!
    ) {
      priceRecommendations(input: {priceRecommendationQueries: $priceRecommendationQueries}) {
        priceRecommendations {
          canonicalProductId
          conditionUuid
          priceLow {
            amountCents
            amount
            currency
          }
          priceHigh {
            amountCents
            amount
            currency
          }
        }
      }
      me @skip(if: $loggedOut) {
        uuid
        shop {
          _id
          currency
        }
      }
      exchangeRates(input: { inverseReverseRates: true }) {
        rates {
          from
          to
          rate
        }
      }
    }
  `,
  {
    skip: ({ canonicalProduct, conditionSlug }) => !canonicalProduct?.id || !conditionSlug,
    options: ({ conditionSlug, canonicalProduct, user }) => {
      const canonicalProductId = canonicalProduct?.id && Number(canonicalProduct.id).toString();
      const conditionUuids = [conditionUuidForSlug(conditionSlug)];

      const priceRecommendationQueries = conditionUuids.filter(Boolean).map(conditionUuid => ({
        canonicalProductId,
        conditionUuid,
      }));

      return {
        ssr: false,
        variables: {
          loggedOut: user.loggedOut,
          priceRecommendationQueries,
        },
      };
    },
  },
);

export default withUserContext(withQuery(RecommendedPriceRange));
